import { AxiosError } from 'axios';
import { useState } from 'react';
import { HashLoader } from 'react-spinners';
import { useSchemaStore } from 'stores/schema-store';

import { AmenityData } from 'api/get-amenities';
import { useGetAmenityReservations } from 'api/get-amenity-reservation';
import { useGetBuilding } from 'api/get-building';

import { WidgetAmenityDataModal } from 'components/modals/widget-amenity-data-modal';
import { PaginatedData } from 'components/paginated-data/paginated-data';

import * as S from './widget-amenity.styled';
import { WidgetDropdown } from '../widget-dropdown';
import { AmenityHeader } from './amenity-header/amenity-header';
import { AmenityReservationSlot } from './amenity-reservation-slot/amenity-reservation-slot';
import { getStartOfDayInTimezone } from './utils';

interface WidgetAmenityProps {
  amenity: AmenityData;
  widgetId: string;
}

export const WidgetAmenity = ({ amenity, widgetId }: WidgetAmenityProps) => {
  const { data: building } = useGetBuilding();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const {
    data: reservations = [],
    isLoading,
    error,
  } = useGetAmenityReservations(amenity.id, getStartOfDayInTimezone(building?.timezone));

  const schema = useSchemaStore((state) => state.schema);
  const setSchema = useSchemaStore((state) => state.setSchema);

  const renderAmenities = () => {
    if (isLoading) {
      return (
        <div className="loading">
          <HashLoader />
        </div>
      );
    }

    if (error) {
      const axiosError = error as AxiosError;
      const message = axiosError.response?.data?.message ?? 'Something went wrong. Please try again';
      return <div className="no-reservations">{message}.</div>;
    }

    if (reservations.length === 0) {
      return <div className="no-reservations">No slots available today</div>;
    }

    return (
      <div className="amenity-reservations-list-wrapper">
        <div className="amenity-reservations-list-header">
          Schedule <b>Today</b>
        </div>
        <PaginatedData
          data={reservations}
          itemsPerSlide={14}
          renderItems={(items) => (
            <div className="amenity-reservations-list-wrapper">
              {items.map((item) => (
                <AmenityReservationSlot
                  key={item.startTime}
                  reservation={item}
                  interval={amenity.timeInterval}
                  capacity={amenity.capacity}
                />
              ))}
            </div>
          )}
        />
      </div>
    );
  };

  const onEdit = (data: AmenityData) => {
    const newSchema = schema.map((item) => {
      if (item.widgetId === widgetId) {
        return {
          ...item,
          data,
        };
      }

      return item;
    });

    setSchema(newSchema);
    setIsEditModalOpen(false);
  };

  return (
    <>
      <S.WidgetWrapper>
        <AmenityHeader amenity={amenity} />
        {renderAmenities()}
      </S.WidgetWrapper>
      <WidgetDropdown widgetId={widgetId} onEdit={() => setIsEditModalOpen(true)} />
      {isEditModalOpen ? (
        <WidgetAmenityDataModal
          amenity={amenity}
          isOpen
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={onEdit}
          submitText="Save"
        />
      ) : null}
    </>
  );
};
